// Shared loan-report engine — report ledger table listing the monthly
// report instances for one discipline (Insurance / UCC). Three states:
// skeleton, empty (with a generate quick-action when permitted), inline
// error with retry. Superseded rows are muted; the filed document links
// to the loan's Documents tab (there is no standalone document viewer
// route — the staff DMS surface for a loan is ?tab=documents).

import type { KeyboardEvent } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  EmptyState,
  SkeletonList,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import type {
  LoanReportDiscipline,
  LoanReportDto,
  LoanReportStatus,
} from "@/api/portfolioAdmin/loanReportsApi";

/** "Jun 2026" — parsed from the period-start calendar date, never shifted
 *  by the viewer's timezone (a "2026-06-01" instant formatted in Phoenix
 *  would render as May). */
export function formatPeriod(periodStart: string): string {
  const [y, m] = periodStart.slice(0, 10).split("-").map(Number);
  if (!y || !m) return "—";
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function disciplineLabel(discipline: LoanReportDiscipline): string {
  return discipline === "Ucc"
    ? "UCC"
    : discipline === "Qa"
      ? "QA"
      : discipline === "FinancialReporting"
        ? "Financial Reporting"
        : "Insurance";
}

// Draft / (PendingReview) / Certified / Filed is a ladder, not a severity
// scale — draft is muted, pending review in-flight, certified informational,
// filed success.
const STATUS_BADGE: Record<
  LoanReportStatus,
  { status: "archived" | "review" | "info" | "finalized"; label: string }
> = {
  Draft: { status: "archived", label: "Draft" },
  PendingReview: { status: "review", label: "Pending review" },
  Certified: { status: "info", label: "Certified" },
  Filed: { status: "finalized", label: "Filed" },
};

export function LoanReportStatusBadge({ status }: { status: LoanReportStatus }) {
  const cfg = STATUS_BADGE[status];
  return <Badge status={cfg.status}>{cfg.label}</Badge>;
}

interface LoanReportLedgerProps {
  loanId: string;
  reports: LoanReportDto[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onRowClick: (report: LoanReportDto) => void;
  /** Empty-state quick action; omit when the viewer lacks Loans.Manage. */
  onGenerate?: () => void;
}

export function LoanReportLedger({
  loanId,
  reports,
  isLoading,
  isError,
  onRetry,
  onRowClick,
  onGenerate,
}: LoanReportLedgerProps) {
  if (isLoading) {
    return <SkeletonList rows={3} data-testid="loan-reports-skeleton" />;
  }

  if (isError) {
    return (
      <div
        className="flex items-center gap-space-3"
        data-testid="loan-reports-error"
      >
        <p className="text-small text-status-error m-0" role="alert">
          Could not load reports.
        </p>
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Retry
        </Button>
      </div>
    );
  }

  const rows = reports ?? [];
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No reports filed yet"
        description="Generate the first monthly report."
        action={
          onGenerate
            ? { label: "Generate report", onClick: onGenerate }
            : undefined
        }
        className="shadow-none border-0"
      />
    );
  }

  // A row is superseded when a newer row's supersedesId points at it.
  const supersededIds = new Set(
    rows.map((r) => r.supersedesId).filter((id): id is string => !!id),
  );

  const onRowKeyDown = (
    e: KeyboardEvent<HTMLTableRowElement>,
    report: LoanReportDto,
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onRowClick(report);
    }
  };

  return (
    <Table data-testid="loan-reports-ledger">
      <THead>
        <TR>
          <TH className="text-white-pure">Period</TH>
          <TH className="text-white-pure">Status</TH>
          <TH className="text-white-pure">Prepared by</TH>
          <TH className="text-white-pure">Certified by</TH>
          <TH className="text-white-pure">Filed document</TH>
        </TR>
      </THead>
      <TBody>
        {rows.map((r) => {
          const superseded = supersededIds.has(r.id);
          return (
            <TR
              key={r.id}
              onClick={() => onRowClick(r)}
              onKeyDown={(e) => onRowKeyDown(e, r)}
              tabIndex={0}
              aria-label={`Open ${disciplineLabel(r.discipline)} report for ${formatPeriod(r.periodStart)}`}
              className={`cursor-pointer ${superseded ? "opacity-60" : ""}`}
              data-testid={`loan-report-row-${r.id}`}
            >
              <TD>
                {formatPeriod(r.periodStart)}
                {superseded && (
                  <span className="ml-space-2 text-caption text-gray-mid italic">
                    Superseded
                  </span>
                )}
              </TD>
              <TD>
                <LoanReportStatusBadge status={r.status} />
              </TD>
              <TD>{r.preparedByUserName ?? "—"}</TD>
              <TD>{r.certifiedByUserName ?? "—"}</TD>
              <TD>
                {r.filedDocumentId ? (
                  <Link
                    to={`/portfolio-admin/loans/${loanId}?tab=documents`}
                    className="text-gold-shadow hover:underline underline-offset-4"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`loan-report-document-link-${r.id}`}
                  >
                    View document
                  </Link>
                ) : (
                  "—"
                )}
              </TD>
            </TR>
          );
        })}
      </TBody>
    </Table>
  );
}
